/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * The platform's currency catalog, and how to render an amount in one.
 *
 * WHY THIS IS NOT A LOOKUP TABLE. Every other picker on the event form is
 * backed by an ApplicationTable and loaded through useEventLookups, which
 * POSTs to /arcee/application/table/fetch/. Currency is not one of those: it
 * is an Arcee Django model, platform-wide, with no row in any application's
 * schema. So it has its own GET endpoint and its own module, and it cannot be
 * resolved the way `registration_type` is — as a related block the data-source
 * layer walks — because that machinery only traverses ApplicationTables.
 *
 * WHAT event_platform_registration.currency_id HOLDS is the `id` from that
 * endpoint. Not the short_code: the column is an integer, and the code is what
 * gets shown, never what gets stored.
 *
 * NO SYMBOLS HERE. utils/currencies.js carries a static ISO list with symbols
 * for the public record pages, and this deliberately does not duplicate it. An
 * event's price renders as `INR 500.00` — the code the organizer actually
 * chose, from the row that actually exists — rather than a glyph looked up in
 * a second table that can disagree with the first.
 */

/** Shape used before anything has loaded, so callers never branch on null. */
export const EMPTY_CATALOG = { list: [], byId: new Map() };

/**
 * Normalises the endpoint's reply into {list, byId}.
 *
 * Tolerates a bare array and the {results}/{records} envelopes, for the same
 * reason readLookup does: endpoints in this codebase have returned all three.
 */
export function readCurrencies(payload) {
  const rows = Array.isArray(payload)
    ? payload
    : (payload?.results || payload?.records || payload?.data || []);

  const list = rows.filter((row) => row && row.id !== undefined && row.short_code);
  return {
    list,
    byId: new Map(list.map((row) => [String(row.id), row])),
  };
}

/** {list} -> [{value, label}] for an OrgSelect. */
export function currencyOptions(catalog) {
  return (catalog?.list || []).map((currency) => ({
    value: currency.id,
    // Code first: it is the short thing an organizer scans for, and the one
    // that will appear beside every price on the event page.
    label: `${currency.short_code} — ${currency.name}`,
  }));
}

/** The row for an id, or null. Ids arrive as both number and string. */
export function currencyById(catalog, currencyId) {
  if (currencyId === undefined || currencyId === null || currencyId === '') return null;
  return catalog?.byId?.get(String(currencyId)) || null;
}

/**
 * The currency an event's prices are in — the id it stored, or the only
 * currency there is.
 *
 * WHY THE FALLBACK IS NOT A GUESS. An event written before currency_id
 * existed has none, and an organizer who never saw the picker never chose
 * one. If the platform has seeded exactly ONE currency, every price on it is
 * necessarily in that currency — there was nothing else to charge in. Naming
 * it is a statement of fact, not an assumption.
 *
 * It also expires by itself. The moment a second currency is seeded the
 * fallback stops applying, because "the only one there is" is no longer true,
 * and those old rows go back to rendering bare numbers — the safe direction,
 * and a visible prompt to set a currency on them rather than a wrong code
 * quietly attached to a price.
 *
 * A stored id always wins, so an event that states its currency is never
 * overridden by this.
 */
export function resolveEventCurrency(catalog, currencyId) {
  const stated = currencyById(catalog, currencyId);
  if (stated) return stated;

  const list = catalog?.list || [];
  return list.length === 1 ? list[0] : null;
}

/**
 * An amount rendered in one currency: `INR 500.00`.
 *
 * The digit count comes from the currency's own `decimal_places`, never a
 * hardcoded 2 — that is the whole reason the endpoint returns it. JPY is a
 * 0-decimal currency and BHD a 3-decimal one, and printing either to two
 * places is wrong in a way that looks deliberate.
 *
 * An unknown currency still renders the NUMBER, without a code. A price with
 * no code beside it is merely incomplete; hiding the price because the catalog
 * has not loaded tells a runner the event is free.
 */
export function formatAmount(amount, currency) {
  if (amount === undefined || amount === null || amount === '') return amount;
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return amount;

  const places = Number.isInteger(currency?.decimal_places) ? currency.decimal_places : 2;
  const fixed = numeric.toFixed(places);
  return currency?.short_code ? `${currency.short_code} ${fixed}` : fixed;
}
